/**
 * Traduz as mensagens de erro do Cognito, que chegam em inglês.
 *
 * Elas vêm da AWS e apareciam cruas na tela ("Incorrect username or password.",
 * "User is not confirmed."), no meio de um produto inteiro em português. Cada
 * tela que mostrava erro de login tinha que lembrar de traduzir a sua, e a de
 * escolher o perfil não lembrava.
 *
 * Traduzir no ponto em que o erro nasce resolve para todas de uma vez: o
 * `auth-service` passa todo erro do Cognito (entrar, pedir o código e trocar a
 * senha) por `erroDoCognitoEmPortugues`, e a tela recebe a frase pronta.
 *
 * O erro do Cognito que não está nas listas vira a frase genérica da ação, e não
 * o texto da AWS: ele vinha em inglês, não dizia nada a quem usa o app e podia
 * trazer detalhe de dentro da AWS, como o erro de uma Lambda do pool. Quem
 * investiga continua com o motivo: o nome do erro vai em `data.codigo`.
 */

const MUITAS_TENTATIVAS = "Muitas tentativas seguidas. Espere alguns minutos e tente de novo."
const CONTA_NAO_ATIVADA = "Esta conta ainda não foi ativada. Procure o convite no seu e-mail."
const SEM_CONEXAO = "Sem conexão. Confira a internet e tente de novo."

/* Código errado e código vencido têm a mesma saída (conferir o e-mail ou pedir
   outro), então dizem a mesma frase: a tela não conta a quem chuta números se o
   código ainda vale. A resposta do Cognito, que o navegador chama direto, segue
   com o nome do erro, e quem segura o chute é o limite de tentativas dele
   (`LimitExceededException`). */
export const CODIGO_ERRADO_OU_VENCIDO =
  "O código está errado ou já venceu. Confira o e-mail ou peça outro."

/* Pelo trecho da mensagem: o mesmo nome de erro (NotAuthorizedException) cobre
   senha errada, conta desativada, convite vencido e outros, e só o texto separa. */
const TRADUCOES: { contem: string; frase: string }[] = [
  { contem: "Incorrect username or password", frase: "E-mail ou senha incorretos." },
  { contem: "User does not exist", frase: "Não existe conta com este e-mail." },
  { contem: "User is not confirmed", frase: CONTA_NAO_ATIVADA },
  { contem: "User is disabled", frase: "Esta conta está desativada. Fale com o suporte." },
  { contem: "Password attempts exceeded", frase: MUITAS_TENTATIVAS },
  { contem: "Attempt limit exceeded", frase: MUITAS_TENTATIVAS },
  {
    contem: "Temporary password has expired",
    frase: "O convite expirou. Peça ao seu personal para enviar outro.",
  },
  /* O aluno convidado que nunca ativou a conta ainda está na senha provisória, e
     o Cognito não troca a senha de quem está nesse estado. */
  { contem: "cannot be reset in the current state", frase: CONTA_NAO_ATIVADA },
  {
    contem: "no registered/verified email",
    frase: "Esta conta não tem um e-mail confirmado para receber o código. Fale com o suporte.",
  },
  {
    contem: "Invalid session for the user",
    frase: "A sessão expirou. Comece de novo.",
  },
  { contem: "Network error", frase: SEM_CONEXAO },
]

/* Pelo nome do erro, quando ele sozinho já diz o que houve. */
const POR_CODIGO: Partial<Record<string, string>> = {
  UserNotFoundException: "Não existe conta com este e-mail.",
  UserNotConfirmedException: CONTA_NAO_ATIVADA,
  PasswordResetRequiredException: 'Por segurança, crie uma senha nova em "Esqueci a senha".',
  CodeMismatchException: CODIGO_ERRADO_OU_VENCIDO,
  ExpiredCodeException: CODIGO_ERRADO_OU_VENCIDO,
  InvalidPasswordException: "A senha precisa de 8 caracteres, com maiúscula, minúscula, número e símbolo.",
  CodeDeliveryFailureException:
    "Não conseguimos mandar o e-mail com o código. Tente de novo em alguns minutos.",
  LimitExceededException: MUITAS_TENTATIVAS,
  TooManyRequestsException: MUITAS_TENTATIVAS,
  TooManyFailedAttemptsException: MUITAS_TENTATIVAS,
  NetworkError: SEM_CONEXAO,
}

const FRASE_GENERICA = {
  entrar: "Não conseguimos entrar. Tente de novo.",
  "pedir-codigo": "Não conseguimos mandar o código. Tente de novo em alguns minutos.",
  "trocar-senha": "Não conseguimos trocar a senha. Tente de novo.",
} as const

export type AcaoNoCognito = keyof typeof FRASE_GENERICA

/** O erro do SDK do Cognito (`code` e `message`) vira a frase da tela. Nunca devolve o texto da AWS. */
export function erroDoCognitoEmPortugues(err: unknown, acao: AcaoNoCognito): string {
  const { code, message } = (err ?? {}) as { code?: unknown; message?: unknown }
  const mensagem = typeof message === "string" ? message : ""
  const porTrecho = TRADUCOES.find((t) => mensagem.includes(t.contem))
  if (porTrecho) return porTrecho.frase
  const porCodigo = typeof code === "string" ? POR_CODIGO[code] : undefined
  return porCodigo ?? FRASE_GENERICA[acao]
}

/**
 * Para as telas de entrar, que só têm a mensagem na mão. O erro do Cognito já
 * chega traduzido pelo `auth-service`; o resto vem da API, em português e com o
 * motivo que a pessoa precisa para resolver ("Conta bloqueada."), e por isso o
 * que não está na lista passa como veio.
 */
export function erroDeLoginEmPortugues(mensagem?: string | null): string {
  if (!mensagem) return FRASE_GENERICA.entrar
  const achado = TRADUCOES.find((t) => mensagem.includes(t.contem))
  return achado ? achado.frase : mensagem
}
